import { useState } from 'react';

import {
  activateSiteCmsPublicationApi,
  getSiteCmsAdminDomainApi,
  listSiteCmsAdminPublicationsApi,
  reserveSiteCmsDomainApi,
  revokeSiteCmsPublicationApi,
  stageSiteCmsPublicationApi,
  verifySiteCmsDomainApi,
} from '@/lib/api/siteCmsApi';
import type { SiteCmsOwnerType } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

type Props = { ownerType: SiteCmsOwnerType; ownerId: number };

/** SUPER_ADMIN only. Domain proof never activates Nginx or DNS/TLS automatically. */
export default function CmsDomainControl({ ownerType, ownerId }: Props) {
  const [open, setOpen] = useState(false);
  const [editedHostname, setEditedHostname] = useState<string | null>(null);
  const [showReleases, setShowReleases] = useState(false);
  const queryClient = useQueryClient();
  const key = ['site-cms', 'admin-domain', ownerType, ownerId];
  const domain = useQuery({
    queryKey: key,
    queryFn: () => getSiteCmsAdminDomainApi(ownerType, ownerId),
    enabled: open,
    retry: false,
  });
  const reserve = useMutation({
    mutationFn: (hostname: string) => reserveSiteCmsDomainApi(ownerType, ownerId, hostname),
    onSuccess: () => {
      setEditedHostname(null);
      void queryClient.invalidateQueries({ queryKey: key });
      toast.success('Domaine réservé : preuve DNS requise avant préparation.');
    },
    onError: error => toast.error(error instanceof Error ? error.message : 'Réservation impossible'),
  });
  const verify = useMutation({
    mutationFn: () => verifySiteCmsDomainApi(ownerType, ownerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      toast.success('Preuve DNS vérifiée. Le site n’est pas encore public.');
    },
    onError: error => toast.error(error instanceof Error ? error.message : 'Preuve DNS non validée'),
  });
  const releasesKey = ['site-cms', 'admin-releases', ownerType, ownerId];
  const releases = useQuery({
    queryKey: releasesKey,
    queryFn: () => listSiteCmsAdminPublicationsApi(ownerType, ownerId),
    enabled: open && showReleases,
    retry: false,
  });
  const stage = useMutation({
    mutationFn: stageSiteCmsPublicationApi,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: releasesKey });
      toast.success('Version préparée sur le serveur privé, hors ligne.');
    },
    onError: error => toast.error(error instanceof Error ? error.message : 'Staging impossible'),
  });
  const activate = useMutation({
    mutationFn: activateSiteCmsPublicationApi,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: releasesKey });
      toast.success('Publication activée. Vérifiez son accès HTTPS.');
    },
    onError: error => toast.error(error instanceof Error ? error.message : 'Activation impossible'),
  });
  const revoke = useMutation({
    mutationFn: () => revokeSiteCmsPublicationApi(ownerType, ownerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: releasesKey });
      toast.success('Diffusion publique retirée.');
    },
    onError: error => toast.error(error instanceof Error ? error.message : 'Révocation impossible'),
  });
  const current = domain.data;
  const hostname = editedHostname ?? current?.hostname ?? '';

  return (
    <div className='mt-3 rounded-xl border border-slate-200 p-3'>
      <button type='button' className='text-sm font-semibold text-blue-700 underline' onClick={() => setOpen(!open)}>
        {open ? 'Fermer la gestion du domaine' : 'Configurer le domaine du site (Super Admin)'}
      </button>
      {open && (
        <div className='mt-3 space-y-3 text-sm'>
          {domain.isPending ? <p>Chargement du domaine…</p> : null}
          {domain.isError ? <p role='alert'>Impossible de lire le domaine.</p> : null}
          {current && (
            <>
              <label className='block font-semibold text-slate-700'>
                Nom de domaine exact
                <input
                  aria-label='Nom de domaine CMS'
                  className='mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal'
                  value={hostname}
                  onChange={event => setEditedHostname(event.target.value)}
                  placeholder='exemple.sites.boulesconnect.fr'
                />
              </label>
              <button
                type='button'
                disabled={!hostname.trim() || reserve.isPending || hostname === current.hostname}
                className='rounded-lg bg-blue-700 px-3 py-2 font-semibold text-white disabled:opacity-50'
                onClick={() => reserve.mutate(hostname)}
              >
                {reserve.isPending && <Loader2 className='mr-1 inline h-4 w-4 animate-spin' />}
                Réserver le domaine
              </button>
              {current.hostname && (
                <div className='space-y-2 rounded-lg bg-slate-50 p-3'>
                  <p className='font-semibold'>{current.verifiedAt ? 'Preuve DNS validée' : 'Preuve DNS à ajouter'}</p>
                  {current.verifiedAt ? <CheckCircle className='h-5 w-5 text-green-600' /> : null}
                  <p>
                    Nom TXT : <code className='break-all'>{current.verificationRecord}</code>
                  </p>
                  <p>
                    Valeur TXT : <code className='break-all'>{current.verificationValue}</code>
                  </p>
                  {!current.verifiedAt && (
                    <button
                      type='button'
                      disabled={verify.isPending}
                      className='rounded-lg border border-slate-300 px-3 py-2 font-semibold'
                      onClick={() => verify.mutate()}
                    >
                      Vérifier le DNS
                    </button>
                  )}
                </div>
              )}
              <p className='text-xs text-slate-500'>
                La vérification DNS n’active ni le certificat TLS ni la diffusion publique.
              </p>
              <div className='rounded-lg border border-amber-200 bg-amber-50 p-3'>
                <button
                  type='button'
                  className='font-semibold text-amber-900 underline'
                  onClick={() => setShowReleases(value => !value)}
                >
                  {showReleases ? 'Masquer les versions de site' : 'Gérer les versions du site (Super Admin)'}
                </button>
                {showReleases && (
                  <div className='mt-3 space-y-3'>
                    <p className='text-xs text-amber-900'>
                      Activation réservée aux domaines explicitement approuvés dans la configuration DNS/TLS du
                      publisher. Ne l’activez pas avant cette validation.
                    </p>
                    {releases.isPending && <p>Chargement des versions…</p>}
                    {releases.isError && <p role='alert'>Versions indisponibles.</p>}
                    {(releases.data ?? []).map(release => (
                      <div
                        key={release.id}
                        className='flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-2'
                      >
                        <span className='text-xs'>
                          Révision {release.revision} · {release.status} · {release.hostname}
                        </span>
                        {release.status === 'PREPARED' && (
                          <button
                            type='button'
                            disabled={stage.isPending}
                            className='rounded border px-2 py-1 font-semibold'
                            onClick={() => stage.mutate(release.id)}
                          >
                            Préparer les fichiers
                          </button>
                        )}
                        {release.status === 'STAGED' && (
                          <button
                            type='button'
                            disabled={activate.isPending}
                            className='rounded border border-emerald-500 px-2 py-1 font-semibold text-emerald-700'
                            onClick={() => {
                              if (
                                window.confirm(
                                  'DNS, certificat HTTPS et routage Nginx du domaine sont-ils validés par l’opérateur ?',
                                )
                              )
                                activate.mutate(release.id);
                            }}
                          >
                            Activer cette version
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type='button'
                      disabled={revoke.isPending}
                      className='rounded border border-red-400 px-3 py-2 font-semibold text-red-700'
                      onClick={() => {
                        if (window.confirm('Retirer immédiatement les pages et PDF publics de ce site ?'))
                          revoke.mutate();
                      }}
                    >
                      Retirer le site du web
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
